import { useState, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { Checkbox } from "components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import * as Yup from "yup";

import confirmationUploadIcon from "../../assets/svg/confirmation-upload-icon.svg";
import { getAllDiscussionDetails, saveConfirmation } from "api/discussion";
import { getWorkflowStatus } from "api/applications";
import { toast } from "sonner";
import type { SaveConfirmationPayload } from "api/discussion";

const DEFAULT_TYPES_OF_MARK = ["Word", "Label", "Logo", "Slogan"];

type ConfirmationFromClientViewProps = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
  onSavingChange?: (saving: boolean) => void;
  onSentStatusChange?: (sent: boolean) => void;
};

type ClassTypeState = { classNumber: number; types: string[] };

const validationSchema = Yup.object({
  numberOfApplications: Yup.number()
    .required("Required")
    .min(1, "Must be at least 1")
    .integer("Must be a whole number"),
  expeditedApplication: Yup.boolean().required("Required"),
});

export default function ConfirmationFromClientView({
  leadId,
  onSuccess,
  onSavingChange,
  onSentStatusChange,
}: ConfirmationFromClientViewProps) {
  const [loading, setLoading] = useState(!!leadId);
  const [classes, setClasses] = useState<{ classNumber: number; goodsServices: string[] }[]>([]);
  const [markTypeOptions, setMarkTypeOptions] = useState<string[]>(DEFAULT_TYPES_OF_MARK);
  const [initialClassTypes, setInitialClassTypes] = useState<ClassTypeState[]>([]);
  const [initialNoOfApplications, setInitialNoOfApplications] = useState(1);
  const [initialExpedited, setInitialExpedited] = useState(false);
  const [hasUserEditedApplications, setHasUserEditedApplications] = useState(false);
  const [hasSentWhatsapp, setHasSentWhatsapp] = useState(false);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      setClasses([]);
      setInitialClassTypes([]);
      setInitialNoOfApplications(1);
      setInitialExpedited(false);
      setHasUserEditedApplications(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getAllDiscussionDetails(leadId)
      .then((data) => {
        if (cancelled) return;
        const cls = data.classes ?? [];
        setClasses(cls.map((c) => ({ classNumber: c.classNumber, goodsServices: c.goodsServices ?? [] })));
        const options = data.proposal?.typesOfMarks?.length
          ? data.proposal.typesOfMarks
          : DEFAULT_TYPES_OF_MARK;
        setMarkTypeOptions(options);

        const conf = data.confirmation;
        if (conf?.selectedClasses?.length) {
          setInitialClassTypes(
            conf.selectedClasses.map((sc) => ({
              classNumber: sc.classNumber,
              types: Array.isArray(sc.typesOfMark) ? sc.typesOfMark : [],
            }))
          );
          setInitialNoOfApplications(conf.noOfApplications ?? 1);
          setInitialExpedited(conf.expeditedApplication ?? false);
          // Treat existing confirmation count as user-entered to avoid auto-overwriting it
          setHasUserEditedApplications(true);
        } else {
          const defaultClassTypes: ClassTypeState[] = cls.map((c) => ({
            classNumber: c.classNumber,
            types: Array.isArray(c.typesOfMark) && c.typesOfMark.length > 0 ? c.typesOfMark : [options[0] ?? "Word"],
          }));
          setInitialClassTypes(defaultClassTypes);
          const autoFromTypes = defaultClassTypes.reduce(
            (sum, ct) => sum + (Array.isArray(ct.types) ? ct.types.length : 0),
            0
          );
          setInitialNoOfApplications(autoFromTypes > 0 ? autoFromTypes : 1);
          setInitialExpedited(false);
          setHasUserEditedApplications(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(err instanceof Error ? err.message : "Failed to load details");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  useEffect(() => {
    if (!leadId) {
      setHasSentWhatsapp(false);
      onSentStatusChange?.(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const status = await getWorkflowStatus(leadId);
        const step = status.steps.find(
          (s) =>
            s.slug === "S2_04_FILING_DETAILS_CONFIRMATION" ||
            s.slug === "filing-details-confirmation"
        );
        if (!cancelled && step) {
          setHasSentWhatsapp(step.isCompleted);
          onSentStatusChange?.(step.isCompleted);
        }
      } catch {
        if (!cancelled) {
          setHasSentWhatsapp(false);
          onSentStatusChange?.(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, onSentStatusChange]);

  if (!leadId) {
    return (
      <div className="max-w-[1085px] w-full flex flex-col gap-5" style={{ fontFamily: "Inter, sans-serif" }}>
        <p className="text-xs text-[#6D6D6D]">Please complete the previous steps to access this form.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-[1085px] w-full flex items-center justify-center py-12" style={{ fontFamily: "Inter, sans-serif" }}>
        <p className="text-sm text-[#6D6D6D]">Loading confirmation details...</p>
      </div>
    );
  }

  if (classes.length === 0) {
    return (
      <div className="max-w-[1085px] w-full flex flex-col gap-5" style={{ fontFamily: "Inter, sans-serif" }}>
        <p className="text-xs text-[#6D6D6D]">
          Complete the Class list step first to add classes for confirmation.
        </p>
      </div>
    );
  }

  const initialValues = {
    classTypes: initialClassTypes,
    numberOfApplications: initialNoOfApplications,
    expeditedApplication: initialExpedited,
  };

  const calculateApplicationsFromTypes = (classTypes: ClassTypeState[]): number => {
    const total = classTypes.reduce(
      (sum, ct) => sum + (Array.isArray(ct.types) ? ct.types.length : 0),
      0
    );
    return total > 0 ? total : 1;
  };

  return (
    <div
      className="max-w-[1085px] w-full flex flex-col gap-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* Uploader */}
      <div
        className="flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-4 sm:gap-5 p-4 sm:p-5 border border-dashed rounded-[12px] sm:rounded-[16px] bg-[#E6F2FF] border-[#222222] min-h-[80px] sm:min-h-[92px]"
      >
        <div className="flex flex-row items-center gap-4 sm:gap-5 flex-1 min-w-0 w-full">
          <div className="w-10 h-10 sm:w-[52px] sm:h-[52px] flex-shrink-0 flex items-center justify-center">
            <img
              src={confirmationUploadIcon}
              alt=""
              className="w-10 h-10 sm:w-[52px] sm:h-[52px] object-contain"
            />
          </div>
          <div className="flex flex-col gap-1 sm:gap-3 min-w-0 flex-1">
            <p className="text-sm sm:text-base font-medium text-[#101111] leading-4" style={{ letterSpacing: "0.01em" }}>
              Confirmation
            </p>
            <p className="text-[12px] sm:text-sm font-normal text-[#7D7D7D] leading-5 sm:leading-[21px]">
              Confirm details agreed with client. Defaults fetched from Proposal.
            </p>
          </div>
        </div>
      </div>

      <Formik
        key={leadId}
        initialValues={initialValues}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={async (values, { setStatus, setSubmitting }) => {
          if (!leadId) return;
          onSavingChange?.(true);
          try {
            const payload: SaveConfirmationPayload = {
              selectedTypesOfMark: values.classTypes.map((ct) => ({
                classNumber: ct.classNumber,
                types: ct.types.filter(Boolean),
              })).filter((ct) => ct.types.length > 0),
              noOfApplications: Number(values.numberOfApplications) || 1,
              expeditedApplication: String(values.expeditedApplication) === "true",
            };
            if (payload.selectedTypesOfMark.length === 0) {
              setStatus({ type: "error", message: "Select at least one class with type of mark." });
              return;
            }
            await saveConfirmation(leadId, payload);
            if (!hasSentWhatsapp) {
              setHasSentWhatsapp(true);
              onSentStatusChange?.(true);
              toast.success("Confirmation saved successfully.");
            } else {
              toast.success("Confirmation saved successfully.");
            }
            onSuccess?.(leadId);
          } catch (err) {
            const message = err instanceof Error ? err.message : "Failed to save confirmation.";
            setStatus({ type: "error", message });
            toast.error(message);
          } finally {
            setSubmitting(false);
            onSavingChange?.(false);
          }
        }}
      >
        {({ values, setFieldValue, status }) => (
          <Form id="confirmation-form" className="flex flex-col gap-4 sm:gap-[22px] w-full">
            {/* Per-class: Selected Class | Selected Type of Mark */}
            {classes.map((cls, idx) => {
              const ct = values.classTypes[idx] ?? { classNumber: cls.classNumber, types: [markTypeOptions[0] ?? "Word"] };
              return (
                <div key={cls.classNumber} className="flex flex-col md:flex-row md:flex-wrap items-stretch gap-4 md:gap-5 w-full p-4 md:p-0 rounded-[6px] border border-[#EEE] md:border-0 bg-white md:bg-transparent">
                  <div className="flex flex-col gap-1 flex-1 min-w-0">
                    <label className="text-xs font-normal text-[#222222] leading-5" style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}>
                      Selected Class(es)
                    </label>
                    <div className="w-full min-h-[44px] md:min-h-0 h-9 px-2.5 py-2 text-[12px] md:text-xs font-normal border border-[#EEEEEE] rounded-[6px] bg-[#FCFCFC] text-[#222222] flex items-center">
                      Class {cls.classNumber}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 flex-1 min-w-0">
                    <span className="text-xs font-normal text-[#222222] leading-5" style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}>
                      Selected Type of Mark
                    </span>
                    <div className="flex flex-wrap gap-x-5 gap-y-2">
                      {markTypeOptions.map((opt) => (
                        <label
                          key={opt}
                          htmlFor={`confirmation-type-${cls.classNumber}-${opt}`}
                          className="flex cursor-pointer items-center gap-2 text-[12px] md:text-xs font-normal leading-5 text-[#222222] min-h-[44px] md:min-h-0 touch-manipulation"
                          style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
                        >
                          <Checkbox
                            id={`confirmation-type-${cls.classNumber}-${opt}`}
                            checked={ct.types.includes(opt)}
                            onCheckedChange={(checked) => {
                              const next = [...values.classTypes];
                              if (!next[idx]) next[idx] = { classNumber: cls.classNumber, types: [] };
                              if (checked) {
                                next[idx] = {
                                  ...next[idx],
                                  types: next[idx].types.includes(opt)
                                    ? next[idx].types
                                    : [...next[idx].types, opt],
                                };
                              } else {
                                next[idx] = {
                                  ...next[idx],
                                  types: next[idx].types.filter((v) => v !== opt),
                                };
                              }
                          setFieldValue("classTypes", next);
                          if (!hasUserEditedApplications) {
                            const autoCount = calculateApplicationsFromTypes(next);
                            setFieldValue("numberOfApplications", autoCount);
                          }
                            }}
                          />
                          {opt}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* No. of Applications | Expedited Application */}
            <div className="flex flex-col md:flex-row md:flex-wrap items-stretch gap-4 md:gap-5 w-full">
              <div className="flex flex-col gap-1 flex-1 min-w-0">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2">
                    <label
                      className="text-xs font-normal text-[#222222] leading-5"
                      style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
                    >
                      No. of Applications
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        const autoCount = calculateApplicationsFromTypes(values.classTypes);
                        setFieldValue("numberOfApplications", autoCount);
                        setHasUserEditedApplications(false);
                      }}
                      className="text-[10px] font-medium text-[#222222] underline underline-offset-2 hover:text-black text-left sm:text-right py-1 min-h-[44px] sm:min-h-0 flex items-center touch-manipulation"
                    >
                      Auto-calc from Types of Mark
                    </button>
                  </div>
                  <Field name="numberOfApplications">
                    {({ field, form }: { field: { value: number | string }; form: { setFieldValue: (name: string, value: unknown) => void } }) => (
                      <input
                        {...field}
                        type="number"
                        min={1}
                        placeholder="1"
                        onChange={(e) => {
                          setHasUserEditedApplications(true);
                          form.setFieldValue("numberOfApplications", e.target.value);
                        }}
                        className="w-full min-h-[44px] md:min-h-0 h-9 px-2.5 py-2 text-[12px] md:text-xs font-normal border border-[#EEEEEE] rounded-[6px] bg-white focus:outline-none focus:ring-1 focus:ring-[#222] placeholder:text-[#6D6D6D]"
                        style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
                      />
                    )}
                  </Field>
                <ErrorMessage name="numberOfApplications" component="div" className="text-xs text-red-500 mt-0.5" />
              </div>
              <div className="flex flex-col gap-1 flex-1 min-w-0">
                <label className="text-xs font-normal text-[#222222] leading-5" style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}>
                  Expedited Application
                </label>
                <Field name="expeditedApplication">
                  {({ field, form }: { field: { value: boolean }; form: { setFieldValue: (n: string, v: boolean) => void } }) => (
                    <Select
                      value={String(field.value)}
                      onValueChange={(v) => form.setFieldValue("expeditedApplication", v === "true")}
                    >
                      <SelectTrigger className="h-9 min-h-[44px] md:min-h-0 w-full text-[12px] md:text-sm rounded-[6px]">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="false">No</SelectItem>
                        <SelectItem value="true">Yes</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </Field>
              </div>
            </div>

            {status?.type === "success" && (
              <div className="text-xs text-green-600">
                {status.message}
              </div>
            )}
          </Form>
        )}
      </Formik>
    </div>
  );
}
